import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

export function ValueProposition() {
  const { currentLanguage } = useLanguage();

  const content = {
    en: {
      label: 'Our Approach',
      title: 'We don\'t just build software.',
      subtitle: 'We architect systems.',
      points: [
        {
          num: '01',
          title: 'Engineering-First Mindset',
          desc: 'Every project starts with architecture. We design systems that are built to last, scale, and evolve with your business.'
        },
        {
          num: '02',
          title: 'Production-Grade Quality',
          desc: 'No prototypes, no shortcuts. We deliver battle-tested code that performs under real-world conditions from day one.'
        },
        {
          num: '03',
          title: 'Long-Term Partnership',
          desc: 'We work as an extension of your team. Clear communication, shared goals, and continuous improvement.'
        }
      ]
    },
    tr: {
      label: 'Yaklaşımımız',
      title: 'Sadece yazılım yapmıyoruz.',
      subtitle: 'Sistem mimarisi kuruyoruz.',
      points: [
        {
          num: '01',
          title: 'Mühendislik Odaklı Zihniyet',
          desc: 'Her proje mimariyle başlar. Dayanıklı, ölçeklenebilir ve işinizle birlikte evrilecek sistemler tasarlıyoruz.'
        },
        {
          num: '02',
          title: 'Production Seviye Kalite',
          desc: 'Prototip yok, kestirme yok. İlk günden gerçek dünya koşullarında çalışan, test edilmiş kod teslim ediyoruz.'
        },
        {
          num: '03',
          title: 'Uzun Vadeli Ortaklık',
          desc: 'Ekibinizin bir uzantısı olarak çalışıyoruz. Net iletişim, ortak hedefler ve sürekli iyileştirme.'
        }
      ]
    },
    de: {
      label: 'Unser Ansatz',
      title: 'Wir bauen nicht nur Software.',
      subtitle: 'Wir entwerfen Systeme.',
      points: [
        {
          num: '01',
          title: 'Engineering-First Mentalität',
          desc: 'Jedes Projekt beginnt mit Architektur. Wir entwerfen Systeme, die gebaut sind um zu bestehen, zu skalieren und sich mit Ihrem Unternehmen zu entwickeln.'
        },
        {
          num: '02',
          title: 'Production-Grade Qualität',
          desc: 'Keine Prototypen, keine Abkürzungen. Wir liefern kampferprobten Code der unter realen Bedingungen vom ersten Tag an performt.'
        },
        {
          num: '03',
          title: 'Langfristige Partnerschaft',
          desc: 'Wir arbeiten als Erweiterung Ihres Teams. Klare Kommunikation, gemeinsame Ziele und kontinuierliche Verbesserung.'
        }
      ]
    }
  };

  const t = content[currentLanguage] || content.en;

  return (
    <section
      id="value"
      data-testid="value-section"
      className="relative py-20 md:py-28"
      style={{ background: 'var(--bg-light)' }}
    >
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8">
        {/* Header - Large statement */}
        <div className="mb-16 md:mb-20">
          <span 
            className="text-xs uppercase tracking-widest mb-6 block"
            style={{ color: '#0F766E' }}
          >
            {t.label}
          </span>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight"
            style={{ color: 'var(--text-on-light-primary)' }}
          >
            {t.title}
            <br />
            <span style={{ color: '#0F766E' }}>{t.subtitle}</span>
          </h2>
        </div>

        {/* Points - Numbered list with left border accent */}
        <div className="space-y-0">
          {t.points.map((point, idx) => (
            <div 
              key={idx}
              className="group flex gap-6 md:gap-10 py-8 border-b transition-all duration-300"
              style={{ borderColor: 'rgba(15, 118, 110, 0.15)' }}
            >
              {/* Number */}
              <div className="flex-shrink-0">
                <span 
                  className="text-3xl md:text-4xl font-bold"
                  style={{ 
                    color: 'rgba(15, 118, 110, 0.3)',
                    fontFamily: 'JetBrains Mono, monospace'
                  }}
                >
                  {point.num}
                </span>
              </div>
              
              {/* Content */}
              <div className="flex-1">
                <h3 
                  className="text-lg md:text-xl font-semibold mb-2 group-hover:text-[#0F766E] transition-colors"
                  style={{ color: 'var(--text-on-light-primary)' }}
                >
                  {point.title}
                </h3>
                <p 
                  className="text-base leading-relaxed max-w-2xl"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {point.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
